<script lang="ts">
    import { defineComponent, h, PropType } from 'vue';

    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import { useUserStore } from '../../../auth/store/user';
    import { UserState } from '../../../auth/types';

    // this guard will display the component if user has required features
    // by default user must have all required features to display the component
    // if requiredAny is turned on, if user has at least one required features, the component will be displayed
    export default defineComponent({
        name: 'RbacDisplayGuard',
        // @vue/compat's RENDER_FUNCTION compat (on by default under MODE 2) used to wrap
        // ANY Options-API `render()` whose declared arity was < 2 (finishComponentSetup
        // -> convertLegacyRenderFn), since it couldn't distinguish a modern zero-arg
        // Vue 3 render() from a legacy Vue 2 `render(h)`. Once wrapped, `this.$slots`
        // returned a Proxy that auto-invoked slot functions on property access
        // (mimicking Vue 2's array-based $slots) instead of the real Vue 3 slots object
        // -- so this component's idiomatic `$slots.default?.()` call ended up calling
        // `.call` on an already-resolved VNode array. This render function is genuine
        // Vue 3 (imports `h` directly, calls slots as functions), so it was opted out of
        // the legacy-wrapping path via the officially documented per-component compat
        // escape hatch, not a workaround. Real bug: this broke slot rendering under
        // compat in the actual running app, not just in tests (PRODUCT-26110). @vue/compat
        // was removed in PRODUCT-26135; this option now survives as an inert leftover,
        // which plain Vue 3 ignores.
        compatConfig: { RENDER_FUNCTION: false },
        props: {
            features: {
                type: Array as PropType<RbacFeature[]>,
                required: true,
            },
            relyOnLoadingUserPermissions: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            requiredAny: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        computed: {
            userStore(): UserState {
                return useUserStore();
            },
            isLoadingUserPermissions(): boolean {
                return false;
            },
            userFeatures(): Record<RbacFeature, boolean> | null {
                return this.userStore.currentRpRbacFeatures;
            },
            display(): boolean {
                if (this.requiredAny) {
                    return this.features.some((feature) => this.userFeatures?.[feature] ?? false);
                }
                return this.features.every((feature) => this.userFeatures?.[feature] ?? false);
            },
        },
        render() {
            const { isLoadingUserPermissions, display, relyOnLoadingUserPermissions, $slots } = this as any;
            if (isLoadingUserPermissions && relyOnLoadingUserPermissions) {
                return h('div', 'Loading...');
            }

            if (!display) {
                // Vue 3's `h()` requires at least one argument (a type/vnode) --
                // there's nothing to render here, so return null directly instead of
                // calling it with none (TS2554).
                return $slots.displayIfNoAccess ? $slots.displayIfNoAccess() : null;
            }

            const children = $slots.default?.();
            // A slot call always hands back an array. Returning it unchanged makes this
            // component's root a fragment, and a caller's class or attributes then have no
            // single element to land on -- Vue drops them and warns. Almost every caller
            // wraps one element, so hand that one back directly and the passthrough works.
            return Array.isArray(children) && children.length === 1 ? children[0] : children;
        },
    });
</script>
